/* eslint-disable react-hooks/exhaustive-deps */
import { Avatar, Box, InputBase, Skeleton, Step, StepConnector, StepLabel, Stepper, Tooltip, Typography, useTheme } from "@mui/material";
import React from "react";
import { Icon } from "@iconify/react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import { useParams } from "react-router-dom";
import { styled, keyframes } from '@mui/material/styles';
import { saveWhatsappChatMetaData, searchContactFromLeadRepo, fetchAIWizardStatus, fetchAIConfig } from "src/redux/slices/whatsappV2";
import AISetupWizard from './AISetupWizard/AISetupWizard';
import WhatsappTemplateModal from './WhatsappTemplatePreview';
import { getStyles, getAvatarColor } from "./styles";
import { femaleAvatarList, hashString, maleAvatarList, stringAvatar } from "./whatsappUtil";

// Match the wizard's stepper visual exactly so the configured-state
// summary feels like the wizard collapsed into a single read-only row.
const FeatureConnector = styled(StepConnector)({
    '& .MuiStepConnector-line': {
        borderColor: '#EBEBEB',
        borderTopWidth: 2,
    },
    '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
        borderColor: '#00A76F',
    },
});

function FeatureStepIcon({ completed }) {
    return (
        <Box sx={{
            width: 24, height: 24, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700,
            backgroundColor: completed ? '#00A76F' : '#EBEBEB',
            color: completed ? '#fff' : '#B0B0B0',
        }}>
            {completed ? '✓' : '✕'}
        </Box>
    );
}

// Gentle pulse for the status dot
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const AIDot = styled(Box, { shouldForwardProp: (prop) => prop !== 'active' })(({ active }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: active ? '#22C55E' : '#919EAB',
    animation: active ? `${pulse} 2s ease-in-out infinite` : 'none',
    flexShrink: 0,
}));

const SearchLeadForWhatsapp = () => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const dispatch = useDispatch();
    const { practiceId } = useParams();

    const {
        leadSearchResult,
        whatsappSearchLeadLoading,
        isLoading,
        aiWizardStatus,
        aiWizardConfig,
        filterCounts,
        whatsppTemplates,
        currentBusinessId,
    } =
        useSelector(
            (state) => ({
                leadSearchResult: state?.whatsappV2?.leadSearchResult,
                whatsappSearchLeadLoading: state?.whatsappV2?.whatsappSearchLeadLoading,
                isLoading: state?.whatsappV2?.isLoading,
                aiWizardStatus: state?.whatsappV2?.aiWizardStatus,
                aiWizardConfig: state?.whatsappV2?.aiWizardConfig,
                filterCounts: state?.whatsappV2?.filterCounts,
                whatsppTemplates: state?.whatsappV2?.whatsppTemplates,
                currentBusinessId: state?.whatsappV2?.currentBusinessId,
            }),
            shallowEqual
        );

    const [wizardOpen, setWizardOpen] = React.useState(false);
    // Tracks an explicit user-close so that a background refetch doesn't
    // yank the wizard back open. Bug 1 of the AI flicker RCA.
    const userClosedWizardRef = React.useRef(false);
    // Tracks whether we've ever received a successful AI status response,
    // so we can show a skeleton on FIRST load instead of the "no AI" empty
    // state. Bug 2 of the AI flicker RCA.
    const [hasLoadedAiStatus, setHasLoadedAiStatus] = React.useState(false);

    // Fetch wizard status on mount only. We deliberately do NOT refetch on
    // every window focus — that was Bug 3 (caused thrash + reopen flicker).
    React.useEffect(() => {
        if (practiceId) {
            dispatch(fetchAIWizardStatus(practiceId));
        }
    }, [practiceId]);

    // Mark "loaded" the moment status is non-null (success or empty).
    React.useEffect(() => {
        if (aiWizardStatus !== null && aiWizardStatus !== undefined) {
            setHasLoadedAiStatus(true);
        }
    }, [aiWizardStatus]);

    // Auto-open wizard when feature enabled but not yet configured; close if disabled.
    // Respect userClosedWizardRef so a refetch doesn't reopen against the user's wish.
    React.useEffect(() => {
        if (aiWizardStatus?.wizard_enabled && !aiWizardStatus?.wizard_completed) {
            dispatch(fetchAIConfig(practiceId));
            if (!userClosedWizardRef.current) setWizardOpen(true);
        }
        if (aiWizardStatus?.wizard_enabled && aiWizardStatus?.wizard_completed) {
            dispatch(fetchAIConfig(practiceId));
        }
        if (aiWizardStatus && !aiWizardStatus?.wizard_enabled) {
            setWizardOpen(false);
        }
    }, [aiWizardStatus, aiWizardStatus?.wizard_completed]);

    const [searchKey, setSearchKey] = React.useState('');

    const handleContactSearch = React.useCallback(
        debounce((val) => {
            dispatch(searchContactFromLeadRepo(val));
        }, 300),
        []
    );

    const handleNewContactClick = (contact, fullName, phone, avatarUrl) => {
        dispatch(saveWhatsappChatMetaData({ leadId: contact?.id, name: fullName, phoneNumber: phone, avatar: avatarUrl }));
    };

    const wizardEnabled = aiWizardStatus?.wizard_enabled;
    const wizardCompleted = aiWizardStatus?.wizard_completed;
    const isAiActive = wizardCompleted && aiWizardStatus?.ai_master_toggle !== false;

    // Only show AI section when feature enabled and not actively searching
    const showAISection = wizardEnabled && searchKey.length === 0 && !whatsappSearchLeadLoading;
    // True while we're still waiting for the very first wizard-status response.
    // Used to render a skeleton instead of the "no AI" fallback so the panel
    // doesn't flash "AI not configured" at every mount.
    const aiStatusLoading = !hasLoadedAiStatus && searchKey.length === 0 && !whatsappSearchLeadLoading;

    // Templates derived from the whatsappV2 slice (already loaded by ChatPage
    // initialisation when a business id is known). Empty until then — we
    // gracefully degrade to a "—" count rather than firing a new fetch.
    const templateList = Array.isArray(whatsppTemplates?.[currentBusinessId])
        ? whatsppTemplates[currentBusinessId]
        : [];
    const templateCount = templateList.length;

    // Status-bucket the templates so the Templates section can surface
    // "needs attention" prominently and show approved/pending/rejected
    // counts at a glance.
    const norm = (t) => String(t?.status || '').toUpperCase();
    const templateApproved = templateList.filter((t) => norm(t) === 'APPROVED');
    const templatePending = templateList.filter((t) => norm(t) === 'PENDING' || norm(t) === 'IN_APPEAL' || norm(t) === 'PENDING_DELETION');
    const templateRejected = templateList.filter((t) => norm(t) === 'REJECTED' || norm(t) === 'DISABLED' || norm(t) === 'PAUSED');
    // Most-used proxy: prefer Meta-provided usage if available; otherwise
    // fall back to recently-created order (templateList is typically
    // returned newest-first by the BE).
    const quickSendTemplates = (templateList.length > 0
        ? [...templateList].sort((a, b) => (b?.use_count ?? 0) - (a?.use_count ?? 0))
        : []
    ).filter((t) => norm(t) !== 'REJECTED' && norm(t) !== 'DISABLED').slice(0, 3);

    // Category → emoji + colour for the quick-send tile glyph. Falls back
    // to a generic document icon if the category is unknown.
    const categoryGlyph = (cat) => {
        const k = String(cat || '').toUpperCase();
        if (k.includes('UTILITY')) return { icon: 'mdi:calendar-check-outline', color: '#3b82f6' };
        if (k.includes('MARKETING')) return { icon: 'mdi:megaphone-outline', color: '#f59e0b' };
        if (k.includes('AUTHENTICATION')) return { icon: 'mdi:shield-key-outline', color: '#8b5cf6' };
        return { icon: 'mdi:file-document-outline', color: '#64748b' };
    };
    const statusDot = (status) => {
        const k = String(status || '').toUpperCase();
        if (k === 'APPROVED') return '#22c55e';
        if (k === 'PENDING' || k === 'IN_APPEAL') return '#f59e0b';
        if (k === 'REJECTED' || k === 'DISABLED' || k === 'PAUSED') return '#ef4444';
        return '#94a3b8';
    };

    // Local template-modal state — re-uses the same component the chat header
    // and composer toolbar use, so create/edit/send paths are identical.
    const [templateModalOpen, setTemplateModalOpen] = React.useState(false);
    const [templateModalIndex, setTemplateModalIndex] = React.useState(0);

    // Solid KPI card — iOS-style: solid white fill, soft radius, hairline
    // border. Responsive: tight on narrow panels, generous padding on
    // wider screens. No translucency — widgets must be easy to read.
    const StatCard = ({ label, value, icon, color, info }) => (
        <Box sx={{
            flex: 1,
            minWidth: 0,
            borderRadius: { xs: '14px', md: '16px' },
            backgroundColor: '#ffffff',
            border: '1px solid rgba(145, 158, 171, 0.16)',
            px: { xs: '8px', md: '12px' },
            py: { xs: '7px', md: '10px' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '2px', md: '4px' },
            position: 'relative',
            overflow: 'hidden',
            transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 220ms',
            '&:hover': {
                transform: 'translateY(-1px)',
                borderColor: 'rgba(145, 158, 171, 0.32)',
            },
        }}>

            <Tooltip title={info || ''} arrow placement="top" disableHoverListener={!info}
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '5px', md: '6px' }, cursor: info ? 'help' : 'default' }}>
                    <Box sx={{
                        width: { xs: 16, md: 20 },
                        height: { xs: 16, md: 20 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: `${color}15`,
                        color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon icon={icon} width="60%" height="60%" />
                    </Box>
                    <Typography sx={{
                        fontSize: { xs: '0.6875rem', md: '0.75rem' },
                        fontWeight: 600,
                        color: '#637381',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {label}
                    </Typography>
                    {info && (
                        <Icon icon="mdi:information-outline" width={11} height={11} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                    )}
                </Box>
            </Tooltip>
            <Typography sx={{
                fontSize: { xs: '0.9375rem', md: '1.125rem', lg: '1.25rem' },
                fontWeight: 700,
                lineHeight: 1.1,
                color: '#212B36',
                // Dim 0 / "—" so real values stand out at a glance.
                opacity: (value === 0 || value === '—') ? 0.45 : 1,
            }}>
                {value ?? '—'}
            </Typography>
        </Box>
    );

    // Solid section surface — iOS-style soft fill, generous radius, hairline
    // border. No translucency or backdrop-blur (those made the widgets hard
    // to read). Clean white card on the page tint.
    const glassSx = {
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        border: '1px solid rgba(145, 158, 171, 0.16)',
        overflow: 'hidden',
    };

    return (
        <Box sx={{
            ...styles.containerStyle,
            // Soft off-white page tint so cards have a subtle contrast edge
            // without going to a hard grey.
            backgroundColor: '#fafbfc',
        }}>
            <Box
                sx={{
                    // Responsive padding & gap — tighter on narrow panels,
                    // more breathing room on bigger screens.
                    p: { xs: '1rem', md: '1.25rem', lg: '1.5rem 1.75rem' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: '0.875rem', md: '1.125rem', lg: '1.5rem' },
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    // Soft max-width so the Hub doesn't sprawl on very wide
                    // screens — keeps the row proportions readable.
                    width: '100%',
                    maxWidth: { xs: '100%', xl: '900px' },
                    mx: 'auto',
                }}
            >
                {/* Compact section label — same uppercase 10px style the
                    ResolvePanel uses for its section labels (SUB_LABEL). */}
                <Box>
                    <Typography sx={{
                        fontSize: '10px',
                        color: '#919EAB',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        fontWeight: 600,
                        mb: '6px',
                    }}>
                        Start new conversation
                    </Typography>
                    {/* Search Input — Resolve-panel TextField style: 10px radius,
                        12px font, hairline border, accent on focus. */}
                    <Box sx={{ position: 'relative' }}>
                        <Icon
                            icon="mdi:magnify"
                            style={{
                                position: 'absolute',
                                left: '0.625rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#919EAB',
                                width: '0.875rem',
                                height: '0.875rem',
                            }}
                        />
                        <InputBase
                            placeholder="Search by name, phone, or email…"
                            disabled={isLoading}
                            onChange={(e) => {
                                setSearchKey(e.target.value);
                                handleContactSearch(e.target.value);
                            }}
                            sx={{
                                width: '100%',
                                padding: '6px 10px 6px 28px',
                                borderRadius: '10px',
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                fontSize: '12px',
                                border: '1px solid rgba(145, 158, 171, 0.16)',
                                transition: 'all 0.15s',
                                '&:focus-within': {
                                    borderColor: '#00AB55',
                                    backgroundColor: '#fff',
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Always-visible hint right under the search bar — replaces the
                    duplicated copies that previously lived under the AI summary
                    card and under the "not configured" fallback. */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && (
                    <Typography sx={{
                        fontSize: '0.75rem',
                        color: '#919EAB',
                        textAlign: 'center',
                        mt: '-0.5rem',
                    }}>
                        Select a conversation on the left to start messaging
                    </Typography>
                )}

                {/* Activity widgets — only when not actively searching */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Activity
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        {/* Single row — all six KPIs side by side. */}
                        <Box sx={{ display: 'flex', gap: '6px' }}>
                            <StatCard
                                label="Pending"
                                value={filterCounts?.PENDING_COUNT ?? 0}
                                icon="mdi:clock-outline"
                                color="#f59e0b"
                                info="Conversations where the patient sent the last message and is waiting for your reply. Click any row in the sidebar's Pending filter to respond."
                            />
                            <StatCard
                                label="Follow-up"
                                value={filterCounts?.FOLLOW_UP_COUNT ?? 0}
                                icon="mdi:email-arrow-right-outline"
                                color="#3b82f6"
                                info="Threads with unread inbound messages — patients you haven't seen yet. Filter the sidebar to Follow-up to see them."
                            />
                            <StatCard
                                label="Window"
                                value={filterCounts?.WINDOW_CLOSED_COUNT ?? '—'}
                                icon="mdi:lock-clock"
                                color="#f97316"
                                info="Threads whose 24-hour service window has expired. You can only deliver an approved template message to these patients until they reply."
                            />
                            <StatCard
                                label="New leads"
                                value={filterCounts?.NEW_LEAD_FILTER ?? '—'}
                                icon="mdi:account-plus-outline"
                                color="#22c55e"
                                info="Leads in this practice that haven't been WhatsApped yet — outbound opportunities. Counted from the practice-init fetch."
                            />
                            <StatCard
                                label="Closed"
                                value={filterCounts?.CLOSED_COUNT ?? 0}
                                icon="mdi:check-circle-outline"
                                color="#8b5cf6"
                                info="Conversations you have marked resolved/closed. Useful for tracking throughput."
                            />
                            <StatCard
                                label="Archived"
                                value={filterCounts?.ARCHIVED_COUNT ?? 0}
                                icon="mdi:archive-outline"
                                color="#64748b"
                                info="Total conversations archived from this WhatsApp inbox. Patient leads still exist and can be restored from the Archived filter in the sidebar."
                            />
                        </Box>
                    </Box>
                )}

                {/* Loading State */}
                {whatsappSearchLeadLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {[1, 2, 3].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.875rem 1rem',
                                    backgroundColor: 'white',
                                    borderRadius: '0.625rem',
                                    border: '1px solid rgba(145, 158, 171, 0.2)',
                                }}
                            >
                                <Skeleton variant="circular" width={48} height={48} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton width="50%" height={20} sx={{ mb: '0.25rem' }} />
                                    <Skeleton width="30%" height={16} sx={{ mb: '0.25rem' }} />
                                    <Skeleton width="80%" height={14} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Search Results */}
                {!whatsappSearchLeadLoading && leadSearchResult?.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '0.25rem' }}>
                            Search Results ({leadSearchResult.length})
                        </Typography>
                        {leadSearchResult.map((contact) => {
                            const fullName = `${contact?.first_name ?? ''} ${contact?.last_name ?? ''}`.trim();
                            const phone = contact?.lead_contact?.phone ?? '';
                            const avatarIndex = hashString(fullName || phone);
                            const avatarColor = getAvatarColor(fullName);

                            const avatarUrl = (contact?.salutation?.toLowerCase() ?? "mrs")?.includes("mrs")
                                ? femaleAvatarList[avatarIndex % femaleAvatarList.length]
                                : maleAvatarList[avatarIndex % maleAvatarList.length];

                            return (
                                <Box
                                    key={contact?.id}
                                    onClick={() => handleNewContactClick(contact, fullName, phone, avatarUrl)}
                                    sx={{
                                        border: '1px solid rgba(145, 158, 171, 0.16)',
                                        borderRadius: '10px',
                                        padding: '8px 10px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 167, 111, 0.06)',
                                            borderColor: 'rgba(0, 167, 111, 0.3)',
                                        },
                                    }}
                                >
                                    <Avatar
                                        {...stringAvatar(avatarUrl, phone, {
                                            width: 30,
                                            height: 30,
                                            fontSize: '11px',
                                            backgroundColor: avatarColor,
                                            color: 'white',
                                            fontWeight: 600,
                                        })}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: '12px', color: '#212B36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {fullName || 'Unknown'}
                                            </Typography>
                                            {contact?.is_archived && (
                                                <Box sx={{ backgroundColor: 'rgba(255, 86, 48, 0.12)', color: '#B71D18', fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>
                                                    ARCHIVED
                                                </Box>
                                            )}
                                            {contact?.lead_treatments?.[0]?.treatment && (
                                                <Typography sx={{ fontSize: '10px', color: '#B76E00', backgroundColor: 'rgba(255, 171, 0, 0.12)', px: '5px', py: '1px', borderRadius: '4px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' }}>
                                                    {contact?.lead_treatments?.[0]?.treatment}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: '11px', color: '#919EAB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {phone || 'No phone'}
                                            {contact?.assignees?.[0]?.user?.first_name && ` · ${contact.assignees[0].user.first_name}`}
                                        </Typography>
                                    </Box>
                                    <Icon icon="mdi:chevron-right" width={14} height={14} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Empty Search State */}
                {searchKey.length > 0 && !whatsappSearchLeadLoading && leadSearchResult?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: '3rem' }}>
                        <Icon icon="mdi:account-search-outline" width={48} height={48} style={{ color: '#919EAB', opacity: 0.5 }} />
                        <Typography sx={{ mt: '0.75rem', fontSize: '0.875rem', color: '#637381' }}>
                            No contacts found for &ldquo;{searchKey}&rdquo;
                        </Typography>
                        <Typography sx={{ mt: '0.25rem', fontSize: '0.8125rem', color: '#919EAB' }}>
                            Try a different name or phone number
                        </Typography>
                    </Box>
                )}


                {/* AI status skeleton — only on the very first load, before
                    we know whether AI is configured. Replaces the brief flash
                    of "no AI" while the wizard-status request is in flight. */}
                {aiStatusLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                WhatsApp AI
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <Box sx={{ ...glassSx, p: '12px' }}>
                            <Skeleton variant="text" width="40%" height={20} />
                            <Skeleton variant="rectangular" height={120} sx={{ mt: '8px', borderRadius: '8px' }} />
                        </Box>
                    </Box>
                )}

                {/* AI Setup Wizard — shown when configuring or editing.
                    Same divider header as the configured/loading states so
                    Activity / WhatsApp AI / Templates all read with parallel
                    section structure. */}
                {showAISection && wizardOpen && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                WhatsApp AI
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <AISetupWizard
                            onClose={() => {
                                userClosedWizardRef.current = true;
                                setWizardOpen(false);
                                dispatch(fetchAIWizardStatus(practiceId));
                                dispatch(fetchAIConfig(practiceId));
                            }}
                            practiceId={practiceId}
                            initialConfig={aiWizardConfig}
                        />
                    </Box>
                )}

                {/* AI Active/Configured State — summary card with feature status */}
                {showAISection && wizardCompleted && !wizardOpen && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                WhatsApp AI
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>

                        <Box sx={{
                            ...glassSx,
                            // Active AI gets a soft green wash so it reads as alive.
                            backgroundColor: isAiActive ? 'rgba(0, 167, 111, 0.06)' : '#ffffff',
                        }}>
                            {/* Header with status + edit button */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '8px',
                                        background: isAiActive
                                            ? 'linear-gradient(135deg, #00A76F 0%, #007B55 100%)'
                                            : 'rgba(145, 158, 171, 0.16)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon icon="mdi:robot-outline" width={16} height={16} style={{ color: isAiActive ? '#fff' : '#919EAB' }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#212B36' }}>
                                            WhatsApp AI
                                        </Typography>
                                        <AIDot active={isAiActive} />
                                        <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, color: isAiActive ? '#00A76F' : '#919EAB' }}>
                                            {isAiActive ? 'Active' : 'Paused'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box
                                    component="button"
                                    onClick={() => { dispatch(fetchAIConfig(practiceId)); setWizardOpen(true); }}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        border: '1px solid #EBEBEB', borderRadius: '8px',
                                        background: '#fff', px: 1, py: '4px',
                                        cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600,
                                        color: '#637381', fontFamily: 'inherit',
                                        '&:hover': { borderColor: '#00A76F', color: '#00A76F', background: 'rgba(0, 167, 111, 0.04)' },
                                    }}
                                >
                                    <Icon icon="mdi:pencil-outline" width={12} height={12} />
                                    Edit
                                </Box>
                            </Box>

                            {/* Configured-state feature timeline — same MUI Stepper visual
                                as the AISetupWizard's "step" header at the top of this page.
                                Each feature is a step; "completed" = enabled. Connector lines
                                between two enabled features turn green so the at-a-glance
                                shape of what's on shows up as a single coloured run. */}
                            {aiWizardConfig && (() => {
                                const features = [
                                    { id: 'out_of_hours', label: 'Out of Hours', isOn: !!aiWizardConfig.out_of_hours_enabled },
                                    { id: 'treatments', label: 'Treatments', isOn: !!aiWizardConfig.auto_assign_treatment },
                                    { id: 'bookings', label: 'Bookings', isOn: !!aiWizardConfig.appt_ack_enabled },
                                    { id: 'hours', label: 'Hours', isOn: !!aiWizardConfig.auto_reply_hours },
                                    { id: 'address', label: 'Address', isOn: !!aiWizardConfig.auto_reply_location },
                                    { id: 'urgent', label: 'Urgent', isOn: !!aiWizardConfig.urgent_detect },
                                ];
                                return (
                                    <Box sx={{ px: '10px', py: '10px' }}>
                                        <Stepper
                                            alternativeLabel
                                            nonLinear
                                            connector={<FeatureConnector />}
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    fontSize: '0.625rem',
                                                    color: '#919EAB',
                                                    mt: '4px !important',
                                                    '&.Mui-completed': { color: '#00A76F', fontWeight: 600 },
                                                },
                                            }}
                                        >
                                            {features.map((f) => (
                                                <Step key={f.id} completed={f.isOn} active={false}>
                                                    <Tooltip title={`${f.label} — ${f.isOn ? 'On' : 'Off'}`} arrow placement="top">
                                                        <StepLabel StepIconComponent={FeatureStepIcon}>
                                                            {f.label}
                                                        </StepLabel>
                                                    </Tooltip>
                                                </Step>
                                            ))}
                                        </Stepper>
                                    </Box>
                                );
                            })()}
                        </Box>

                    </Box>
                )}

                {/* Templates section — Option A: status-first hub.
                    Surfaces problems (rejected/paused) prominently and gives
                    operators a 1-click "Quick send" path to the most-used
                    templates, while keeping CRUD entry points in the header.
                    All click-targets re-use the same WhatsappTemplateModal
                    the chat composer's "Browse templates" button uses, so
                    create/edit/send flows are identical. */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Templates
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <Box sx={glassSx}>
                            {/* Header — title + CRUD actions */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '8px',
                                        backgroundColor: 'rgba(0, 167, 111, 0.12)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon icon="mdi:file-document-multiple-outline" width={16} height={16} style={{ color: '#007B55' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#212B36' }}>
                                        Templates
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Tooltip title="Create new template" arrow>
                                        <Box
                                            component="button"
                                            onClick={() => { setTemplateModalIndex(0); setTemplateModalOpen(true); }}
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                border: '1px solid #00A76F', borderRadius: '8px',
                                                background: '#fff', px: 1, py: '4px',
                                                cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600,
                                                color: '#00A76F', fontFamily: 'inherit',
                                                '&:hover': { background: 'rgba(0, 167, 111, 0.06)' },
                                            }}
                                        >
                                            <Icon icon="mdi:plus" width={12} height={12} />
                                            New
                                        </Box>
                                    </Tooltip>
                                    <Box
                                        component="button"
                                        onClick={() => { setTemplateModalIndex(0); setTemplateModalOpen(true); }}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            border: '1px solid #EBEBEB', borderRadius: '8px',
                                            background: '#fff', px: 1, py: '4px',
                                            cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600,
                                            color: '#637381', fontFamily: 'inherit',
                                            '&:hover': { borderColor: '#637381', color: '#212B36' },
                                        }}
                                    >
                                        Browse
                                        <Icon icon="mdi:arrow-right" width={12} height={12} />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Status counts row */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                px: '14px', py: '8px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.06)',
                                fontSize: '0.6875rem',
                                color: '#637381',
                            }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                                    <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#212B36' }}>
                                        {templateApproved.length}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: '0.6875rem' }}>approved</Typography>
                                </Box>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                                    <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#212B36' }}>
                                        {templatePending.length}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: '0.6875rem' }}>pending</Typography>
                                </Box>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444' }} />
                                    <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#212B36' }}>
                                        {templateRejected.length}
                                    </Typography>
                                    <Typography component="span" sx={{ fontSize: '0.6875rem' }}>need attention</Typography>
                                </Box>
                            </Box>

                            {/* Needs-attention banner — only when there's something to fix */}
                            {templateRejected.length > 0 && (
                                <Box sx={{
                                    px: '14px', py: '8px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.06)',
                                    borderBottom: '1px solid rgba(239, 68, 68, 0.12)',
                                    display: 'flex', flexDirection: 'column', gap: '4px',
                                }}>
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Icon icon="mdi:alert-circle-outline" width={12} height={12} />
                                        Needs attention
                                    </Typography>
                                    {templateRejected.slice(0, 2).map((tpl) => {
                                        const idx = templateList.findIndex((t) => t === tpl);
                                        return (
                                            <Box key={tpl?.id ?? tpl?.name}
                                                onClick={() => { setTemplateModalIndex(idx); setTemplateModalOpen(true); }}
                                                sx={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    cursor: 'pointer', py: '2px',
                                                    '&:hover': { '& .tpl-name': { textDecoration: 'underline' } },
                                                }}>
                                                <Typography className="tpl-name" sx={{ fontSize: '0.75rem', color: '#3D3D3D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                                    {tpl?.name ?? 'Untitled'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.625rem', color: '#b91c1c', fontWeight: 600, ml: 1 }}>
                                                    {String(tpl?.status || 'rejected').toLowerCase()}
                                                </Typography>
                                                <Icon icon="mdi:chevron-right" width={14} height={14} style={{ color: '#b91c1c', marginLeft: 4 }} />
                                            </Box>
                                        );
                                    })}
                                    {templateRejected.length > 2 && (
                                        <Typography sx={{ fontSize: '0.625rem', color: '#b91c1c' }}>
                                            +{templateRejected.length - 2} more — click Browse to view all
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Frequent templates — click to preview/edit in the
                                existing modal. Sending requires picking a thread
                                first; we don't try to do recipient-picking here. */}
                            {quickSendTemplates.length > 0 ? (
                                <Box sx={{ p: '10px' }}>
                                    <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.4px', mb: '6px' }}>
                                        Frequent
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: '6px' }}>
                                        {quickSendTemplates.map((tpl) => {
                                            const idx = templateList.findIndex((t) => t === tpl);
                                            const g = categoryGlyph(tpl?.category);
                                            return (
                                                <Tooltip key={tpl?.id ?? tpl?.name} title={`${tpl?.name ?? 'Untitled'} — ${String(tpl?.status || '').toLowerCase()}`} arrow>
                                                    <Box
                                                        onClick={() => { setTemplateModalIndex(idx); setTemplateModalOpen(true); }}
                                                        sx={{
                                                            flex: 1, minWidth: 0,
                                                            cursor: 'pointer',
                                                            borderRadius: '12px',
                                                            border: '1px solid rgba(145, 158, 171, 0.16)',
                                                            backgroundColor: '#ffffff',
                                                            p: '8px',
                                                            display: 'flex', flexDirection: 'column', gap: '4px',
                                                            transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 220ms, background-color 220ms',
                                                            '&:hover': {
                                                                transform: 'translateY(-1px) scale(1.01)',
                                                                borderColor: '#00A76F',
                                                                backgroundColor: 'rgba(0, 167, 111, 0.04)',
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Box sx={{
                                                                width: 22, height: 22, borderRadius: '6px',
                                                                backgroundColor: `${g.color}15`,
                                                                color: g.color,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}>
                                                                <Icon icon={g.icon} width={13} height={13} />
                                                            </Box>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusDot(tpl?.status) }} />
                                                        </Box>
                                                        <Typography sx={{
                                                            fontSize: '0.6875rem', fontWeight: 600, color: '#212B36',
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {tpl?.name ?? 'Untitled'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '0.5625rem', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            {String(tpl?.category || '—').toLowerCase()}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: '1rem', px: '14px' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#919EAB' }}>
                                        {templateCount === 0
                                            ? 'No templates yet — click New to create one or Browse to sync from Meta.'
                                            : 'Open a conversation or click Browse to load templates.'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

            </Box>

            {/* Template browser/manager modal — same component the composer uses */}
            <WhatsappTemplateModal
                open={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
                currentIndex={templateModalIndex}
                templates={templateList}
                onNext={() => setTemplateModalIndex((i) => Math.min(i + 1, templateList.length - 1))}
                onPrevious={() => setTemplateModalIndex((i) => Math.max(i - 1, 0))}
                onSelect={setTemplateModalIndex}
                onSend={() => {
                    // No thread context here — closing the modal is the safest
                    // outcome. Operators wanting to send pick a conversation
                    // first; create/edit work fine without one.
                    setTemplateModalOpen(false);
                }}
            />
        </Box>
    );
};

export default SearchLeadForWhatsapp;
